/**
 * Real-time subscription to the signed-in user's `users/{uid}` document.
 *
 * Surfaces `preferences` (routine toggles / quiet hours / timezone) and the
 * `microsoft` connection metadata used by the Settings route. The document may
 * not exist yet for a brand-new user, in which case `userDoc` is `null`.
 */
import type { UserDoc } from '@agente/shared'
import { onSnapshot } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import { userDoc } from '@/lib/firestore'

export interface UseUserDocResult {
  userDoc: UserDoc | null
  loading: boolean
  error: string | null
}

export function useUserDoc(): UseUserDocResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [value, setValue] = useState<UserDoc | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) {
      setValue(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    const unsubscribe = onSnapshot(
      userDoc(uid),
      (snap) => {
        setValue(snap.exists() ? snap.data() : null)
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
    return unsubscribe
  }, [uid])

  return { userDoc: value, loading, error }
}
